// path: frontend/src/context/CreditProvider.tsx
import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";

import { API_BASE_URL } from "@/lib/api-config";
import { auth } from "@/lib/firebase";

const isDev = import.meta.env.MODE === "development";

export interface CreditCtx {
  balance: number | null;
  isAdmin: boolean;
  packSize: number;
  videoCost: number;
  remaining: number | null;
  limit: number | null;
  loading: boolean;
  error: string | null;
  setCredits: (remaining: number, limit?: number) => void;
  refreshCredits: () => Promise<void>;
  consumeOne: () => void;
}

export const CreditContext = createContext<CreditCtx | undefined>(undefined);

async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (isDev && import.meta.env.VITE_DEV_NO_AUTH === "true") {
    headers.Authorization = "Bearer dev";
    return headers;
  }

  const user = auth?.currentUser;
  if (user) {
    try {
      headers.Authorization = `Bearer ${await user.getIdToken()}`;
    } catch (error) {
      console.error("Failed to get auth token:", error);
    }
  }

  return headers;
}

export function CreditProvider({ children }: { children: React.ReactNode }) {
  const [balance, setBalance] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [packSize, setPackSize] = useState(50);
  const [videoCost, setVideoCost] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    if (!auth) {
      setAuthReady(true);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged(() => {
      setAuthReady(true);
    });

    return () => unsubscribe();
  }, []);

  const refreshCredits = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/payment/credits`, {
        method: "GET",
        headers,
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          setIsAdmin(false);
          setBalance(null);
          setError(null);
          return;
        }

        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${errorText}`
        );
      }

      const data = await response.json();

      setIsAdmin(data?.is_admin === true);
      setBalance(
        typeof data?.credit_balance === "number"
          ? data.credit_balance
          : null
      );
      setPackSize(
        typeof data?.credit_pack_size === "number"
          ? data.credit_pack_size
          : 50
      );
      setVideoCost(
        typeof data?.video_render_cost === "number"
          ? data.video_render_cost
          : 5
      );
    } catch (err: any) {
      console.error("Failed to load credits:", err);
      setError(err?.message || "Failed to load credits");
      setBalance(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authReady) return;
    void refreshCredits();
  }, [authReady, refreshCredits]);

  function setCredits(remaining: number, _limit?: number) {
    setBalance(remaining);
  }

  function consumeOne() {
    setBalance((current) =>
      current === null ? null : Math.max(0, current - videoCost)
    );
  }

  return (
    <CreditContext.Provider
      value={{
        balance,
        isAdmin,
        packSize,
        videoCost,
        remaining: balance,
        limit: null,
        loading,
        error,
        setCredits,
        refreshCredits,
        consumeOne,
      }}
    >
      {children}
    </CreditContext.Provider>
  );
}

export function useCredits() {
  const ctx = React.useContext(CreditContext);
  if (!ctx) throw new Error("useCredits must be used within CreditProvider");
  return ctx;
}

export default CreditProvider;
